import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, TemplateRef, OnChanges, OnInit, AfterViewInit } from '@angular/core';
import { ButtonComponentConfig } from '../../utils/button/button-component-config.interface';
import { HeadingComponentConfig } from '../../utils/heading/heading-component-config.interface';
import { ImageComponentConfig } from '../../utils/image/image-component-config.interface';
import { TextComponentConfig } from '../../utils/text/text-component-config.interface';
import { IconComponentConfig, IconType } from '../../utils/icon';

/* TODO: Add this component to the Rappider components */
@Component({
  selector: 'rappider-card-ccs',
  templateUrl: './card-ccs.component.html',
  styleUrls: ['./card-ccs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RappiderCardCcsComponent implements OnInit, OnChanges, AfterViewInit {

  /* data that will be passed and emitted by the item click */
  @Input() data: any;
  /* title */
  @Input() title: HeadingComponentConfig;
  /* icon name (deprecated - use iconConfig instead), if you pass this input data, the icon will be displayed before the title*/
  @Input() iconName: string | undefined;
  /* icon configuration, if provided this will be used instead of iconName */
  @Input() iconConfig: IconComponentConfig | undefined;
  /* subtitles as array */
  @Input() subtitles: HeadingComponentConfig[];
  /* description */
  @Input() description: TextComponentConfig;
  /* image as cover picture, not the backgound cover but above the title */
  @Input() image: ImageComponentConfig;
  /* cover template instead of image, if you pass image input data, this input will not be displayed */
  @Input() thumbnailTemplate: TemplateRef<any>;
  /* first action button */
  @Input() zerothActionButton: ButtonComponentConfig;
  /* first action button */
  @Input() firstActionButton: ButtonComponentConfig;
  /* second action button */
  @Input() secondActionButton: ButtonComponentConfig;
  /* third action button */
  @Input() thirdActionButton: ButtonComponentConfig;
  /* a color band that adds a colored div at the top of the card content */
  @Input() colorBand?: string;
  @Input() selectedCardIcon: IconComponentConfig;
  @Input() selected: boolean;
  /* top right icon configuration */
  @Input() topRightIcon: IconComponentConfig;

  /* card item select/click */
  @Output() itemSelect = new EventEmitter<any>();
  /* action button click  */
  @Output() actionClick = new EventEmitter();
  @Output() renderCompleted = new EventEmitter();

  /**
   * Check if any action button exists
   */
  get hasActionButtons(): boolean {
    return !!(this.zerothActionButton || this.firstActionButton || this.secondActionButton || this.thirdActionButton);
  }

  ngOnInit(): void {
    this.initDefaults();
  }

  ngOnChanges(): void {
    this.initDefaults();
  }

  ngAfterViewInit(): void {
    this.renderCompleted.emit(true);
  }

  actionClicked(button: ButtonComponentConfig) {
    this.actionClick.emit({ button: button, data: this.data });
  }

  itemClicked() {
    this.itemSelect.emit(this.data);
  }

  initDefaults() {
    if (!this.selectedCardIcon) {
      this.selectedCardIcon = {
        name: 'fa-solid fa-circle-check',
        type: IconType.FontAwesome
      };
    }
    if (this.selected == null) {
      this.selected = false;
    }
  }

  isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  /**
   * Check if content is an object or contains [object Object] string
   * This handles cases where objects are accidentally stringified
   */
  shouldShowJsonViewer(content: any): boolean {
    if (!content) {
      return false;
    }
    if (this.isObject(content) && !Array.isArray(content)) {
      return true;
    }
    if (Array.isArray(content)) {
      return true;
    }
    if (typeof content === 'string' && content.includes('[object Object]')) {
      return true;
    }
    return false;
  }

  /**
   * Get object from content for JSON viewer
   * Returns the object if it's an object, otherwise returns null
   */
  getObjectFromContent(content: any): any {
    if (!content) {
      return null;
    }
    if (this.isObject(content) || Array.isArray(content)) {
      return content;
    }
    if (typeof content === 'string' && content.includes('[object Object]')) {
      return null;
    }
    return null;
  }

  /**
   * Get icon configuration from iconConfig or fallback to iconName as FontAwesome
   */
  getTitleIconConfig(): IconComponentConfig | null {
    if (this.iconConfig) {
      return this.iconConfig;
    }
    if (this.iconName) {
      return {
        name: this.iconName,
        type: IconType.FontAwesome
      };
    }
    return null;
  }
}
